import type { TFunction } from "i18next";

/**
 * Turning the server's refusal into a sentence this player can read.
 *
 * # Why the server does not send the sentence
 *
 * It used to. `src/rpc/game/intent.rs` rendered the engine's `Rejection`
 * straight onto the wire, which meant the one moment the game explains itself —
 * *why* that move was refused — was also the one moment it stopped speaking the
 * player's language. The alternative was a locale on the socket and a
 * translation table in Rust: a second i18n system beside the typed one here,
 * and a notion of language pushed toward an engine that is deliberately pure.
 *
 * So the engine names the refusal (`Rejection::code`) and this owns the words.
 *
 * # An unknown code shows itself
 *
 * A server newer than this bundle can send a code with no key. That renders the
 * raw token — `not_a_thing_yet` — which is ugly and *legible*, and far better
 * than a blank box or a lie. The same path carries genuinely unexpected errors
 * ("party_id is not a uuid"), which are already sentences and are shown as they
 * came.
 */

/**
 * Every refusal the game plane sends.
 *
 * Mirrors `Rejection::code` in `src/game/effect.rs` plus the two registry
 * failures in `refused()`. Hand-kept, like `services/http.ts`'s interfaces —
 * nothing generates it, so a new variant needs a line here and a message in both
 * locales, and until it has one it falls through to rendering its own token.
 */
export const REFUSAL_CODES = [
  "wrong_phase",
  "no_round",
  "game_finished",
  "not_in_game",
  "not_your_turn",
  "cannot_advance",
  "slot_out_of_range",
  "option_out_of_range",
  "already_placed",
  "already_flagged",
  "cannot_afford",
  "not_available",
  "already_challenged",
  "needs_a_target",
  "no_such_card",
  "cannot_target_yourself",
  "no_game",
  "game_ended",
] as const;

/** One of the refusals this build knows how to word. */
export type RefusalCode = (typeof REFUSAL_CODES)[number];

/** Whether the server sent a refusal this build has copy for. */
function isRefusalCode(value: string): value is RefusalCode {
  return (REFUSAL_CODES as readonly string[]).includes(value);
}

/**
 * What to put on screen when the server refuses a move.
 *
 * `t` is threaded in rather than this being a hook, so it stays a pure function
 * of the error — testable without rendering anything, and callable from a
 * `catch` where hooks cannot go.
 */
export function refusalText(cause: unknown, t: TFunction): string {
  const raw = cause instanceof Error ? cause.message.trim() : "";
  if (isRefusalCode(raw)) {
    return t(`rejection.${raw}`);
  }
  // Not a code: either an older server still sending prose, or an error from
  // outside the game plane. Both are already sentences.
  return raw === "" ? t("round.refused") : raw;
}
